'use client';

/**
 * Inline-editable drawer fields (LF-5988 / LEADS-21, `functional-spec.md` §4.3).
 *
 * Plain text at rest, an editor in the same slot on click.
 *
 * ## The row treatment is Attio's (LF-6099)
 *
 * Four things, all requested by the product owner after a side-by-side with
 * Attio's record panel — and each one is load-bearing rather than cosmetic:
 *
 * · **The hover highlight is the whole value cell, not the text.** A tint that
 *   hugs the string makes `Yes` a 24px target and an address a 200px one, so the
 *   affordance is a different size on every row. The cell is a constant target,
 *   and it is where the editor will appear.
 *
 *   This rule was reversed once and reinstated: the VALE-130 follow-up hugged
 *   the value with a floor, and VALE-349 put the target back on the column —
 *   with the one complaint that survived the round trip fixed directly, a
 *   `fullWidth` row capped back to a single column's value width
 *   ({@link ATTRIBUTE_TARGET_CAPPED}). Read that token and
 *   {@link ATTRIBUTE_VALUE_TARGET} before proposing the hug a third time.
 * · **Values are left-aligned.** Right-aligning them gave a card of nineteen
 *   ragged left edges; the eye has to re-find the start of every value.
 * · **The label column is one width for every row, and that width responds to
 *   the grid.** A shared column is what makes the left-alignment worth having:
 *   without it a long label shoves its own value right and the column stops
 *   being a column. Attio's own label cell is 85px of a 296px panel (29%, icon
 *   included), and Vale's labels are longer, icon-less and sentence-case. The
 *   width is `--attr-label-w` (LF-6240) — see {@link ATTRIBUTE_LABEL_WIDTH}, which
 *   carries the measurements. Its ceiling and floor ride the `--spacing`
 *   density knob like the rest of the card.
 *
 *   **A label that needs a prefix to be unambiguous does not belong in this
 *   column** — it belongs in a headed sub-grid whose heading carries the prefix.
 *   The partner / spouse rows are the worked example: as `Second enquirer
 *   first/middle/last name` they needed 160/178/158px here and rendered as three
 *   *identical* `Second enquirer …`, so the reader could not tell which name was
 *   which. Shortening the prefix does not fix that (`Spouse middle name` still
 *   overflows); removing it does, which is why they now render as bare `First
 *   name` / `Middle name` / `Last name` under a `Partner / spouse` heading. See
 *   `spouseFields` in `./lead-field-specs`.
 *
 *   **The column used to be a flat 112px that never yielded** (`w-33 max-w-[40%]
 *   shrink-0`), and the 40% cap it was supposed to yield through was **inert at
 *   every width the grid ships at** — measured 112.2px from a 560px viewport to
 *   a 1920px one, peaking at 39.3% on the narrowest row before the grid
 *   collapses to one column. So neither column ever gave way to the other: two
 *   fixed boxes, each clipping its own overflow. At a 1920 viewport **6 of 25
 *   labels clipped, every one of them beside a value cell with room to spare**
 *   (median 279px unused, and zero values clipping).
 *
 *   That is backwards. A label is a word the reader already knows and can finish
 *   from four characters; the value is the thing they came to read, and it is
 *   the half that cannot be guessed. Attio shrinks the label first for exactly
 *   this reason — its own narrow panel reads `Email ad…` beside a full
 *   `sammy@jmacgregorlega…`. LF-6240 makes the column actually behave that way;
 *   {@link ATTRIBUTE_LABEL_WIDTH} is where that lives.
 * · **A `select` value renders as a subtle `StatusBadge`.** A closed vocabulary looks
 *   different from free text, so `Not asked yet` reads as a chosen state rather
 *   than as something somebody typed. Only `select`: text / number / date /
 *   address stay plain, because a pill around an email is just a box.
 *
 *   **A picker is not automatically a state, and `plainValue` is the opt-out.**
 *   The pill claims *one of a fixed set of states*; deriving it from
 *   `editor.kind === 'select'` claims only *edited by a dropdown*. Two fields are
 *   the second and not the first: **people** (`Intake owner`, `Preferred
 *   solicitor`) — a colleague is a reference, and keying on the editor made the
 *   same solicitor a pill here and plain text as `With` in the
 *   Meeting rail an inch away — and **`Office`**, which is only a picker when the
 *   *firm* has configured an office list, so one tenant saw a pill and another
 *   plain text for the identical field. Genuine states keep it.
 *
 * The palette is **not** Attio's — that reference is dark and Vale is light. The
 * highlight is `slate-50` — deliberately one step weaker than the `slate-100`
 * this surface uses elsewhere, because a pill sitting on `slate-100` disappears
 * into it (see the read-out below).
 *
 * **The editor shell is deliberately NOT inventory's.** Inventory opens a quick-set
 * *modal* because it edits an estate value flowing into the distribution maths; a
 * lead's `Office` is a typo fix, so the editor is an input in the slot. Editor
 * weight scales with the consequence of the edit — which is also why `Address`,
 * being five parts rather than one, opens a small popover instead.
 *
 * Two properties make this faster than a per-card `Edit` pencil rather than merely
 * different, and both live here rather than in each field:
 *
 * · **The grid owns which field is open**, so `Tab` commits and opens the *next*
 *   field instead of escaping to the next card. Three corrections in one pass, not
 *   three click-type-click cycles. `Shift+Tab` steps back.
 * · **`Escape` restores the value the field held when it opened**, and saves
 *   nothing.
 *
 * ## What the prototype left to the build
 *
 * `components/prototypes/leads/editable-field.tsx` had no server, and said so:
 * *"the build needs one — a save that 409s must keep the field open with the
 * typing intact rather than silently reverting."* Both halves of that are here:
 *
 * · **The draft is local and the save fires on commit**, not per keystroke. The
 *   prototype patched local state on every character, which is where §4.3's "edits
 *   apply live" comes from; a PATCH per character is a different thing entirely, so
 *   the draft updates per character (untrimmed — see below) and the request goes on
 *   `Tab` / `Enter` / blur / a select choice. The board underneath updates from that
 *   commit, which is what `useUpdateLead` invalidates the board reads for.
 * · **A rejected save says so, and re-opens the field when that costs nothing.**
 *   The row advances optimistically on `Tab` so a slow link never stalls the
 *   chain; if the save then fails, the grid reports it and brings that field
 *   back with the draft the coordinator typed, because the alternative — a
 *   silently reverted value — reads exactly like a save that worked.
 *
 *   **It stops short of that when she has already started typing somewhere
 *   else** (LF-6250). The draft is one piece of state, so re-opening the failed
 *   field *replaces* whatever is in the editor she moved to — which silently
 *   destroyed real typing. The message is unconditional; moving her is not. The
 *   rule, and the rest of the commit path, is in `./use-attribute-commit`.
 *
 * **Nothing is trimmed on the way through.** §4.3: edits apply per character, so
 * trimming here strips the space the moment it is typed and "Evelyn M Prosser"
 * arrives as "EvelynMProsser". The backend's `emptyStringToNull` is the one place
 * `''` becomes a cleared column.
 *
 * **An empty value renders grey `Set <label>…` placeholder text, not `—`** (and no
 * longer the dashed `Add` pill it shipped with — LF-6099, Attio's treatment). An
 * editable gap should invite, and naming the field in the placeholder invites more
 * specifically than a pill reading `Add` nineteen times over. Board rows keep their
 * plain `—` (§3.4) — a row is a read surface, the drawer is where you work.
 */

import { useEffect, useId, useRef } from 'react';
import { cn } from '@/lib/utils';
import {
  ATTRIBUTE_LABEL,
  ATTRIBUTE_LABEL_COMPACT,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_MOTION,
  ATTRIBUTE_ROW,
  ATTRIBUTE_ROW_TIGHT,
  ATTRIBUTE_ROW_COMPACT,
  ATTRIBUTE_ROW_ERROR,
  ATTRIBUTE_ROW_NOTE,
  ATTRIBUTE_TARGET_CAPPED,
  ATTRIBUTE_VALUE_COLUMN,
  attributeLabelWidthStyle
} from './attribute-tokens';
import { useAttributeSurfaceChannel } from './attribute-unsaved-state';
import { AttributeValueSlot, offersClear } from './attribute-value-cell';
import {
  AttributeFieldControl,
  rendersEditorInPopover
} from './attribute-field-control';
import {
  isLiveBoolean,
  isLiveTriState,
  AttributeTriStateSegmented,
  AttributeBooleanSegmented,
  type BooleanControlSetting
} from './attribute-boolean';
import { AttributeReadOut } from './attribute-read-out';
import { attributeDraftDisplay } from './attribute-draft-display';
import type {
  AutoOpenSignal,
  AttributeDraft,
  AttributeSpec
} from './attribute-types';
import { useAttributeCommit } from './use-attribute-commit';
import { useRowMotion } from './use-row-motion';
import { TitleWithInfo, type InfoHelp } from '@/components/shared/info-button';

/*
  The row spec, its editor union and its draft type now live in
  `./attribute-types`, the read-out in `./attribute-read-out` and the open editor
  in `./attribute-field-control` — this file had grown past the 300-line guideline
  carrying every one of them plus the grid itself. Every type is re-exported below
  for importers inside this folder; everything outside it imports from
  `@/components/attributes` (see `./index.ts`) rather than from here or from a
  deep path.

  `sameDraft` is deliberately NOT among them: LF-6250 gave the commit path its own
  in `./use-attribute-commit`, which is the one that runs. A second copy here would
  be two definitions of one fact.
*/
export type {
  AutoOpenSignal,
  BooleanControl,
  AttributeDraft,
  AttributeEditor,
  AttributeNameParts,
  AttributeOption,
  AttributeSpec,
  LongTextEditor
} from './attribute-types';

/*
  The label column's width, the row's geometry and the motion vocabulary all
  moved to `./attribute-tokens` (LF-6304 / [ATTR-3]) — the one place this
  treatment's measurements are named, so a second surface adopting it imports
  them rather than copying the strings and drifting from them one fix at a
  time. `ATTRIBUTE_LABEL_WIDTH` carries the LF-6240 measurements verbatim.
*/

/**
 * What the `×` commits — {@link AttributeSpec.clearTo}, defaulting to `''`
 * **only when the member is absent** (VALE-307).
 *
 * `clearTo` is optional *and* nullable, and `null` is a declared value rather
 * than a missing one: an address clears to `null`, because five empty strings
 * still read as *something is captured here*. So `field.clearTo ?? ''` — what
 * both call sites below used to write — collapsed that `null` into a string,
 * and every address `toPatch` opens with `if (typeof draft === 'string') return
 * null`. The result was a refusal on the one gesture the row exists for:
 * *"Property address was not saved. That value cannot be saved."* on the
 * Contacts drawer, the inventory drawer, the enquiry and the matter card at
 * once, since all four declare `clearTo: null`.
 *
 * A named helper rather than the test written twice — two copies of one rule is
 * how the `??` survived being wrong in both places (root `CLAUDE.md` § *"Bind to
 * the source of truth"*).
 */
export function clearDraft(field: {
  clearTo?: AttributeDraft;
}): AttributeDraft {
  return field.clearTo === undefined ? '' : field.clearTo;
}

/** The (?) a row carries after its label, if any (GUIDE-20). */
function rowHelp(field: { help?: InfoHelp }): InfoHelp | null {
  return field.help ?? null;
}

/**
 * **The field a read-out should draw, with the just-committed value standing in
 * for a record that has not caught up** (VALE-1773).
 *
 * `field` unchanged in every case but one: `bridge` names this row, and the
 * row's editor kind has a draft formatter. `attributeDraftDisplay` returning
 * `undefined` is that second test — an inline kind has no formatter, so its row
 * keeps the host's `display` and the bridge simply does not apply to it.
 *
 * Only `display` is substituted. `copyText`, `badge` and the affordance
 * predicates stay the host's, because the flash is a fact about the row's
 * *text*, and widening the substitution would mean inventing a draft-derived
 * answer for things the draft does not carry.
 */
function bridgedField(
  field: AttributeSpec<unknown>,
  bridge: { index: number; value: AttributeDraft } | null,
  index: number
): AttributeSpec<unknown> {
  if (!bridge || bridge.index !== index) return field;
  const display = attributeDraftDisplay(field.editor, bridge.value);
  return display === undefined ? field : { ...field, display };
}

export function AttributeGrid<TPatch>({
  fields,
  columns = 2,
  booleanControl = 'segmented',
  legacyChrome = false,
  onEditingChange,
  onSave,
  autoOpen,
  density = 'default',
  rowSpacing = 'default',
  hideEmptyRows = false,
  labelColumn,
  valueColumns,
  testId
}: {
  fields: AttributeSpec<TPatch>[];
  /**
   * Row density (VALE-977). `compact` tightens the row's vertical inset for a
   * block that only reads out — see `ATTRIBUTE_ROW_COMPACT`.
   *
   * **Honoured only when every field is `readOnly`.** The default inset is part
   * of a live row's hit box, so tightening a grid that can be typed into would
   * shrink a target rather than a gap. Asking for it on a live grid is a
   * mistake, and the grid ignores it rather than quietly shrinking the target.
   */
  density?: 'default' | 'compact';
  /**
   * Close up the **vertical** space between rows (VALE-1494), without touching
   * anything else `density` governs.
   *
   * Separate from `density` above because that one is gated
   * on every row being `readOnly` (it shrinks hit targets) and these grids are
   * editable. This only removes the row's own `py-0.5`; the value's own inset,
   * which IS the hit target, is unchanged. See {@link ATTRIBUTE_ROW_TIGHT}.
   *
   * For a grid rendered as one item's detail inside a list, where its row gaps
   * have to read as tighter than the gaps between the list's items.
   */
  rowSpacing?: 'default' | 'tight';
  /**
   * Leave out a row that has no value to read (VALE-977).
   *
   * **Only ever drops a `readOnly` row.** An empty editable row is an
   * invitation — it is how a value gets set — so hiding one would make a field
   * unreachable. A readOnly row with nothing in it can only ever say `—`, and
   * on a surface reading a record that does not exist yet (an extract's
   * document, whose size and upload time are not recorded until it is
   * persisted) that is a column of dashes above the thing you came to look at.
   */
  hideEmptyRows?: boolean;
  /**
   * Which label treatment the rows take, when the default is not the one this
   * surface wants (VALE-1174).
   *
   * The default is unchanged and stays derived: a `compact`, all-`readOnly`
   * grid hugs its labels (`ATTRIBUTE_LABEL_COMPACT`) and every other grid uses
   * the shared fixed-width column (`ATTRIBUTE_LABEL`). VALE-977 chose the hug
   * for the AI-review summary block deliberately, and the reason is written
   * into the token: a shared column reserves the width of the LONGEST label, so
   * `Phone` opposite `Date of birth` makes the two pairs disagree about how far
   * a value sits from its label.
   *
   * `'shared'` buys the opposite property, which is the one a **diff** wants:
   * every value in a column starting at the same x, so a reader scanning what
   * the AI changed compares values rather than hunting for them. The truncation
   * that comes with it (`Account numb…`) is the drawer's own behaviour, not a
   * defect. The width variable is already published by
   * `attributeLabelWidthStyle`; only the label was not consuming it.
   *
   * Additive on purpose — omit it and the grid behaves exactly as it did.
   */
  labelColumn?: 'hug' | 'shared';
  /**
   * Whether the value columns hug their content or split the width evenly
   * (VALE-1174).
   *
   * The default is unchanged and stays derived: a `compact`, all-`readOnly`
   * grid hugs (`max-content`), every other grid splits. Hugging is right for a
   * read-only summary of SHORT values — see the note at the template below.
   *
   * `'split'` is for a grid whose cells are **not** short and whose columns
   * must agree with those of a SIBLING grid. The AI-review detail block renders
   * one grid per row, so hugging sizes each block to its own content and the
   * second column lands at a different x on every row — visible as a stepped
   * column down the list the moment a cell grows (a diff's `new` value plus its
   * struck-through old one is exactly such a cell).
   *
   * Additive on purpose — omit it and the grid behaves exactly as it did.
   */
  valueColumns?: 'hug' | 'split';
  /**
   * Two for the wide left column, one for the 320px rail, three for a
   * full-width surface whose values are short enough that two columns reads as
   * a band of air (VALE-129 — the estate-details card). Explicit rather than
   * responsive: `sm:` keys off the **viewport**, so a `sm:grid-cols-2` grid inside
   * the rail would still split into two 160px columns on any real screen.
   */
  columns?: 1 | 2 | 3;
  /**
   * How a `boolean` row renders — the selector [ATTR-2] published this
   * vocabulary ahead of (LF-6350 / [ATTR-8]).
   *
   * **Defaults to `segmented`** (LF-6388 / [ATTR-10]). It defaulted to `select`
   * while only the Contacts drawer opted in, on the grounds that the shipped
   * enquiry surfaces must not move — but that default was guarding a case that
   * does not exist: **every** `kind: 'boolean'` spec in production is Contacts'
   * (six of them, all in `contact-attribute-specs.ts`), and `lead-field-specs`
   * declares none at all. So the flip moves zero shipped pixels, and a new
   * surface gets the live control rather than a dropdown it has to ask for. See
   * {@link AttributeBooleanSegmented} for why `segmented` and not `switch` /
   * `checkbox`.
   */
  booleanControl?: BooleanControlSetting;
  /**
   * **Holds this surface on the pre-unification chrome until its own conversion
   * ticket. Temporary by construction — grep it to zero** (LF-6388 /
   * [ATTR-10]).
   *
   * This was `refined?: boolean`, an opt-**in** defaulting to `false`, so the
   * unified treatment reached only the surface that remembered to ask for it.
   * That is backwards for a shared component: the *next* surface to adopt this
   * grid would have silently shipped the old look, which is the exact failure
   * the promotion exists to end. So the flag inverted and was renamed for what
   * it now means — `refined` reads like a feature you might want, `legacyChrome`
   * reads like something to remove, and the count of call sites passing it is
   * the migration's progress bar.
   *
   * **Passing it is not a design choice.** There is one treatment per field
   * type and it is the default; a surface that wants a different look changes
   * the shared component for everyone, or does not change it. A *new* surface
   * must never reach for this.
   *
   * What it holds back: the value cell keeps its chrome on the read-out
   * *button* rather than the cell, so the hover tint and the copy / clear
   * affordances vanish the moment the row opens; the open editor draws a white
   * bordered `Input` (which nudges the value 1px right and down) instead of
   * {@link ATTRIBUTE_EDITOR_CHROME}'s ring; and a `date` row opens the native
   * control rather than the `DatePicker`.
   *
   * One call site passes it today — `lead-matter-panel.tsx`, the matter
   * overview's enquiry band, which is a matter tab rather than a drawer and
   * converts on its own ticket. Deleting this flag and the arms it threads is
   * that ticket's payoff.
   */
  legacyChrome?: boolean;
  /**
   * Fires as a field opens and closes, so the drawer can intercept `Escape`.
   * Without it one Escape both reverts the field *and* dismisses the sheet —
   * Radix's dismissable layer owns that key unless the consumer says otherwise.
   */
  onEditingChange?: (editing: boolean) => void;
  /** Sends the patch. Rejecting is what surfaces the failure. */
  onSave: (patch: TPatch) => Promise<unknown>;
  /**
   * Open one field's editor by label, from outside the grid (LF-6374).
   *
   * The Contacts drawer's `Contact type` row uses this to lead the reader
   * straight into the field that completes an incomplete switch — e.g.
   * choosing `Organisation` with no organisation name yet opens `Organisation
   * name` for them, rather than leaving a new required row to be found among
   * the read-outs. Optional and additive: the other three consumers of this
   * grid never pass it, so their behaviour is unchanged.
   */
  autoOpen?: AutoOpenSignal | null;
  testId?: string;
}) {
  /**
   * **Gated rows are dropped from the layout, and that is what makes them
   * inert** (LF-6303 / [ATTR-2], `docs/attribute-treatment.md` §4.2 rule 3).
   *
   * A `hidden` row is declared rather than omitted by the spec builder, so that
   * [ATTR-3]'s motion pass has a before/after to animate between. Filtering it
   * out *here* — before the row list reaches either the commit hook or the
   * render — is what turns that declaration into the rest of the contract at a
   * single stroke: the row takes no pointer events, is absent from the
   * accessibility tree, and is skipped by the `Tab` chain, because the chain
   * steps through indices into this exact array (`useAttributeCommit`'s `step`)
   * and a filtered row has no index to land on.
   *
   * Doing it any later would mean teaching the keyboard chain about `hidden`
   * separately, which is a second place for the two to disagree — and the
   * disagreement would be a Tab stop that renders nothing.
   */
  const visible = fields.filter((field) => {
    if (field.hidden) return false;
    /*
      Folded into THIS filter rather than a second one, for the reason the
      comment above gives: the keyboard chain is built from `visible`, so a row
      dropped anywhere else would leave a Tab stop that renders nothing.

      `display == null || display === ''` is the same test `attribute-read-out`
      applies to decide whether to draw its empty state, so a row dropped here
      is exactly one that would have rendered `—`. Editable rows are never
      dropped — see the prop's own note.
    */
    if (!hideEmptyRows || !field.readOnly) return true;
    return !(field.display == null || field.display === '');
  });

  /**
   * **The unified treatment, stated positively once** (LF-6388 / [ATTR-10]).
   *
   * {@link legacyChrome} is the public prop because it has to read as debt at
   * the call site; every arm below reads better as *"is this the unified
   * treatment?"* than as a double negative, and deriving it here means the two
   * can never disagree. The three components this threads into take it under
   * this same name.
   */
  const unified = !legacyChrome;

  /**
   * The whole open / draft / commit / failure state machine lives in
   * `./use-attribute-commit` (LF-6250) — this component only lays the rows out.
   *
   * It is a hook rather than lines in here because LF-6240 puts a second surface
   * on this treatment, and a save path whose failure mode is losing the user's
   * typing must not be available to reimplement. See that file's header.
   */
  /**
   * **This grid's half of the surface's unsaved state** (VALE-349).
   *
   * A surface mounts one grid per `rows` block, so no single grid can answer
   * *"can this drawer be closed"* — each reports its own answer under an id
   * stable for its lifetime and the provider aggregates. Absent a provider the
   * reporter is a no-op, which is every surface that cannot be closed
   * (`/enquiries/{id}`, a card, a story).
   *
   * The report itself goes through the commit hook **synchronously**, not from
   * an effect here — see `./attribute-unsaved-state` for why that ordering is
   * load-bearing against Radix's `pointerdown` dismiss.
   */
  const {
    report: reportUnsaved,
    reportUncommitted,
    register
  } = useAttributeSurfaceChannel();
  const gridId = useId();

  const {
    openIndex,
    draft,
    failure,
    saving,
    bridge,
    setDraft,
    open,
    revert,
    commitAndClose,
    step,
    flush
  } = useAttributeCommit<TPatch, AttributeDraft>({
    fields: visible,
    onSave,
    onEditingChange,
    onUnsavedChange: (unsaved) => reportUnsaved(gridId, unsaved),
    onUncommittedDraftChange: (holding) => reportUncommitted(gridId, holding)
  });

  /*
    An unmounting grid holds nothing: a drawer that closed over a save in flight
    must not leave the surface it was on looking dirty for the next record.
    Stable deps, so the cleanup runs on unmount and never between renders.
  */
  useEffect(
    () => () => {
      reportUnsaved(gridId, false);
      reportUncommitted(gridId, false);
    },
    [gridId, reportUncommitted, reportUnsaved]
  );

  /*
    **The surface's handle on this grid's open editor** (VALE-1274) — what a
    host's `Save` calls before it submits. Registered in an effect and withdrawn
    on unmount, under the same id the reports use; `flush` is stable, so this
    runs once per grid rather than per render.
  */
  useEffect(() => {
    register(gridId, { flush });
    return () => register(gridId, null);
  }, [flush, gridId, register]);

  /**
   * Consume {@link AutoOpenSignal} once per `token`, so a repeat request for
   * the same label (the reader flips the switch back and forth) still opens
   * it again — see that type's doc for why a token rather than the label
   * alone. `visible` and `open` are new identities most renders (the fields
   * powering this grid are rebuilt on every render, upstream), so the guard
   * has to be the token, not the effect's own dependency list.
   */
  const consumedAutoOpenTokenRef = useRef<number | null>(null);
  useEffect(() => {
    if (!autoOpen || autoOpen.token === consumedAutoOpenTokenRef.current)
      return;
    consumedAutoOpenTokenRef.current = autoOpen.token;
    const index = visible.findIndex((field) => field.label === autoOpen.label);
    if (index >= 0) open(index);
  }, [autoOpen, visible, open]);

  /**
   * **A gated row now animates in and out rather than jumping** (LF-6304 /
   * [ATTR-3], `docs/attribute-treatment.md` §3).
   *
   * The motion is what stops a gate flip reading as the layout being shuffled:
   * the row that arrives fades and drops the last few pixels, the rows that
   * make way glide, and the row that leaves is lifted out of flow and faded
   * *while* the gap closes beneath it rather than after. All four §3 rules —
   * one axis never both, travel capped at 32px, an exit shorter than and
   * front-loaded against the entrance, and the whole thing skipped under
   * `prefers-reduced-motion` — live in the hook and {@link ATTRIBUTE_MOTION}.
   *
   * **Three things this wiring depends on**, each of them a real trap:
   *
   * · **It gates on the same `visible` derivation the commit hook does**, so
   *   there is one answer to "which rows exist" rather than two that can
   *   disagree.
   * · **`rows` is rendered, not `visible`** — that is what keeps a departing
   *   row on screen long enough to fade. Live rows come first in `visible`
   *   order, so index `i` still addresses `visible[i]` and `openIndex` needs no
   *   remapping; a departing row's index sits past the end, which is why it can
   *   never be the open one.
   * · **The container is `relative`** (below). A departing row is placed at
   *   container-relative coordinates, and without a positioned ancestor those
   *   resolve against the `fixed` sheet instead — a row leaving at grid-y 89px
   *   was drawn 89px from the top of the *drawer*, above the first heading.
   *
   * **The enquiry drawer stays motion-free in practice, and that is checked
   * rather than assumed.** No shipped spec sets `hidden`, so its row list never
   * changes and the pass returns at its first guard; and because the hook
   * treats the mount as a baseline-only pass, opening the drawer does not play
   * an entrance on rows that were simply always there.
   */
  const { containerRef, registerRow, rows } = useRowMotion({
    items: visible,
    // `key` when the spec sets one, else the label. A row whose label is
    // conditional must carry a key, or relabelling it reads as a departure plus
    // an arrival and the two render on top of each other — see `AttributeSpec.key`.
    keyOf: (field) => field.key ?? field.label,
    motion: ATTRIBUTE_MOTION
  });

  /*
    `compact` is honoured only on a grid that cannot be typed into — see the
    prop's own note. Derived rather than trusted so a later field added without
    `readOnly` puts the density back to default on its own.
  */
  const allReadOnly = visible.every((field) => field.readOnly);

  return (
    <div data-testid={testId}>
      {/*
        `@container` is what makes the label column one width for every row:
        `--attr-label-w` is resolved against THIS element's inline size, so a
        `fullWidth` row spanning both columns still lands on the same number as
        its neighbours. See {@link ATTRIBUTE_LABEL_WIDTH}.

        `relative` is load-bearing rather than defensive, and belongs on this
        same element: a departing row is lifted out of flow and placed at
        coordinates measured against `containerRef`, so the positioned ancestor
        and the container query have to be the one box.
      */}
      <div
        ref={containerRef}
        className={cn(
          '@container relative grid',
          /*
            The column gutter, and at compact density it is deliberately the
            WIDER of the two gaps a reader sees (VALE-977).

            A label and its value are one thing; the separation that matters is
            between one pair and the next. With a 16px row gap and a 16px
            gutter those two were equal, so the pairs stopped grouping and the
            grid read as four loose columns. Compact splits them — 8px inside a
            pair (`ATTRIBUTE_ROW_COMPACT`), 40px between them.
          */
          density === 'compact' && allReadOnly ? 'gap-x-10' : 'gap-x-8',
          /*
            **Compact columns hug their content instead of splitting the width.**

            An equal-fraction column is right for a record grid: every value cell
            is the same width, so the editors line up and a long value has room
            to grow into. But it means a SHORT value leaves the rest of its half
            empty — which on a read-only summary reads as a gap between the first
            column's value and the second column's label, and no amount of
            tightening the gutter fixes it, because the space is inside the cell
            rather than between the columns.

            `max-content` + `justify-start` closes it: each column is as wide as
            its widest row needs and no wider, so the pairs sit together. The
            label column keeps its own width, so labels still align down the
            grid.
          */
          (valueColumns ??
            (density === 'compact' && allReadOnly ? 'hug' : 'split')) === 'hug'
            ? columns > 1 &&
                'sm:grid-cols-[max-content_max-content] sm:justify-start'
            : cn(
                columns === 2 && 'sm:grid-cols-2',
                columns === 3 && 'sm:grid-cols-3'
              )
        )}
        style={attributeLabelWidthStyle(
          columns,
          density === 'compact' && allReadOnly ? 'compact' : 'default'
        )}
      >
        {rows.map(({ item: field, leaving, key: rowKey }, index) => {
          /*
            **The label describes the value button** (VALE-1505). At rest the
            button's only content is the value, `Miller`, or `Set date of
            birth…`, so a screen reader announced "Miller, button" with no
            field name; every *open* editor already carried
            `aria-label={field.label}`, so the row was named only while being
            edited. The read-out points `aria-describedby` at this span. The id
            is derived from the grid's own `useId` plus the row key, so it is
            stable across renders and unique across the several grids one
            surface mounts.
          */
          const labelId = `${gridId}-label-${rowKey}`.replace(
            /[^A-Za-z0-9_:-]/g,
            '_'
          );
          return (
            <div
              key={rowKey}
              /*
              Filed under the same key the row is keyed by, so the motion pass
              can find the node to measure. `aria-hidden` while it leaves: it is
              inert, absolutely positioned and about to unmount, so it must not
              be readable as part of the record.
            */
              ref={registerRow(rowKey)}
              aria-hidden={leaving || undefined}
              /*
              `min-w-0` is load-bearing, not defensive. This row is a GRID item, and
              a grid item defaults to `min-width: auto` — it refuses to shrink below
              its content's min-content width. An email address is one unbreakable
              word, so the row holds itself wider than the card and pushes the value
              past its edge, and `truncate` never gets a constrained width to
              ellipsise against.
            */
              className={cn(
                // `items-start`, not `items-baseline`: the label truncates, and a
                // flex item with `overflow: hidden` has its baseline synthesised
                // from its bottom margin edge, so baseline alignment would drop
                // every label below its own value. Both sides carry the same
                // leading + vertical inset, which lines the first lines up exactly.
                density === 'compact' && allReadOnly
                  ? ATTRIBUTE_ROW_COMPACT
                  : rowSpacing === 'tight'
                    ? ATTRIBUTE_ROW_TIGHT
                    : ATTRIBUTE_ROW,
                // Spans both columns, but the label stays on the value's first
                // line, the value simply wraps beneath itself. Stacking the label
                // on its own line spends a whole row on two words and leaves the
                // value starting at the card edge, out of line with every other
                // value on the card.
                //
                // Gated on `columns === 2` / `columns === 3`: a one-column grid has
                // no explicit template, so a `col-span` on a single item makes the
                // grid *invent* an extra implicit column and skews every other row.
                // No shipped one-column host has a `fullWidth` field today, which
                // is the only reason this was not already visible.
                //
                // **A prose row spans too, whatever its host decided about
                // `fullWidth`** (VALE-1899). The compact AI review blocks strip
                // `fullWidth` from every row (VALE-1181), so a note sat in half a
                // row beside an unrelated field and a paragraph wrapped into a
                // narrow column. Owner decision: every note takes the whole row.
                // `multiline` is the structural "this is prose" declaration, the
                // same one `ChangedValue` wraps on, so no host has to remember.
                columns === 2 &&
                  (field.fullWidth || field.multiline) &&
                  'sm:col-span-2',
                columns === 3 &&
                  (field.fullWidth || field.multiline) &&
                  'sm:col-span-3',
                // Holds a side-by-side pair together against the auto-flow, see
                // {@link AttributeSpec.startsRow}. Gated on a real template for
                // the same reason the spans above are.
                columns !== 1 && field.startsRow && 'sm:col-start-1'
              )}
            >
              {/*
              One shared width that ellipsises, rather than a label sized to its
              own text. This is the half of the Attio treatment that makes
              left-aligned values work: `Relationship to deceased` and `Phone`
              otherwise start their values 100px apart, and the value column
              stops being a column. `title` carries the full label, since the
              clipped one is all that is on screen.
            */}
              <span
                id={labelId}
                title={field.label}
                /*
                VALE-531: a STRUCTURAL marker for the label cell, because the
                migration made its colour ambiguous. `lead-drawer.test.tsx`
                selected these cells with `.shrink-0.text-slate-400`, and both
                `slate-400` (the label rung) and `slate-500` (a sibling rung) now
                resolve to `text-muted-foreground` — so the selector started
                matching other muted cells too. A test that identifies an element
                by its COLOUR is coupled to the thing this initiative changes;
                this attribute is what it should have keyed on all along.
              */
                data-attribute-label=""
                /*
                `shrink-0` is load-bearing and stays: `--attr-label-w` is
                already the yielded width (it is a function of the grid, see
                {@link ATTRIBUTE_LABEL_WIDTH}), so leaving flex-shrink on would let
                the label lose width a *second* time — weighted against this
                row's own value text, which is what breaks the shared column.
              */
                className={cn(
                  (labelColumn ??
                    (density === 'compact' && allReadOnly
                      ? 'hug'
                      : 'shared')) === 'hug'
                    ? ATTRIBUTE_LABEL_COMPACT
                    : ATTRIBUTE_LABEL,
                  ATTRIBUTE_LEADING
                )}
              >
                <TitleWithInfo title={field.label} help={rowHelp(field)} />
              </span>
              {/*
              **The value half of the row: the cell, and the refusal attached to
              it** (VALE-349). The cell is everything the label column leaves and
              the read-out inside fills it, so the hit area is the value column
              on every row rather than a tint the width of whatever that row
              happens to say. No negative margin any more (and so no `pr-1.5` to
              cancel one): the read-out's own `px-1.5` sits *inside* the cell,
              which is what keeps the highlight from overrunning the card padding
              in a 320px rail.

              **A `fullWidth` row is capped back to one column's value width at
              rest** ({@link ATTRIBUTE_TARGET_CAPPED}), so a residential address
              spanning a three-column card does not carry a tint the width of the
              card.

              **Released on open only for an editor that renders IN the row**
              (VALE-1247). An inline control is typed into, so it wants the span
              the row was given; a popover editor floats over the surface and
              never uses that width, so releasing the cap for one did nothing
              except snap the row out to the full width of the card the instant
              it was clicked — visible on the account and the address, and
              reported as the row resizing itself for no reason. The kind list
              is `rendersEditorInPopover`'s, which lives beside the branches
              that draw those popovers.

              **Except a `multiline` row, which is never capped** (VALE-493). The
              cap and its release were a width *change* on open, and on the one
              kind of row whose editor is inline and grows as it is typed into,
              that change is the most violent thing on the surface: `Matter
              notes` wrapped over 7 lines at one column's width and re-wrapped to
              about 3 the instant it opened, moving every row below it and
              popping the drawer's scrollbar. Uncapped, the value is measured
              against the span it will be edited at, so opening the row changes
              nothing and typing into it wraps a third as often.

              The address keeps its cap: it is `fullWidth` without `multiline`,
              its editor is a popover rather than an inline box, and a short
              address in a wide tint is exactly the defect the cap exists for.
              What the two have in common is that the resting width is the width
              the value is *worked on* at — which for a popover is the cap, and
              for an inline growing editor is the span.
            */}
              <div
                className={cn(
                  ATTRIBUTE_VALUE_COLUMN,
                  /*
                  **Gated on `columns > 1`, exactly as the span above is**
                  (VALE-1419). The cap exists to stop a row that spans *more*
                  than its neighbours carrying a tint wider than its value, so
                  it has a job only where there is a wider span to cap back. A
                  one-column grid gives every row the same width and the span
                  above is already withheld there — so capping made a
                  `fullWidth` row's target **narrower** than every ordinary row
                  beside it, which is the precise inverse of the "every row's
                  target is the same width" rule the cap was written to serve.

                  It went unseen for the reason the span's own note gives: no
                  shipped one-column host had a `fullWidth` field. The Discovery
                  drawer's asset card is the first, and it carries three —
                  `Bank`, `Bank account` and `Notes` — which is what made that
                  column read as ragged.
                */
                  columns > 1 &&
                    field.fullWidth &&
                    !field.multiline &&
                    (openIndex !== index || rendersEditorInPopover(field)) &&
                    ATTRIBUTE_TARGET_CAPPED
                )}
              >
                {/*
                **A `render` row draws its own value** (VALE-863) — see
                {@link AttributeSpec.render}. It has no single value to commit,
                so the slot, its editor and the copy/clear affordances are all
                skipped; everything outside this div (the label column, the
                leading, the grid placement, the motion) is still the grid's.
              */}
                {field.render ? (
                  field.render()
                ) : (
                  <AttributeValueSlot
                    unified={unified}
                    field={field as AttributeSpec<unknown>}
                    open={openIndex === index}
                    live={
                      isLiveBoolean(field, booleanControl) ||
                      isLiveTriState(field)
                    }
                    saving={saving.includes(field.label)}
                    failed={failure?.label === field.label || !!field.error}
                    /*
                On the unified path the **cell** owns the clear affordance, so
                the read-out below is told not to render its own. The commit is
                the identical `open` → `commitAndClose` pair either way — see the
                read-out's `onClear` for why clearing must not be routed around
                the one save path.
              */
                    onClear={
                      unified && offersClear(field as AttributeSpec<unknown>)
                        ? /*
                          `clearOverride` replaces the clear without routing
                          around the save path — it does not save (VALE-1247).
                          See the prop's own doc for the review surfaces that
                          point it at a reject.
                        */
                          (field.clearOverride ??
                          (() => {
                            open(index);
                            commitAndClose(index, clearDraft(field));
                          }))
                        : undefined
                    }
                  >
                    {isLiveTriState(field) ? (
                      /*
                      Same contract as the live boolean below: the control
                      replaces both the read-out and the editor, and the answer
                      still goes through `open` → `commitAndClose` so `validate`
                      can refuse it and a rejected save surfaces on the row.
                    */
                      <AttributeTriStateSegmented
                        field={field}
                        focused={openIndex === index}
                        onCommit={(next) => {
                          open(index);
                          commitAndClose(index, next);
                        }}
                        onStep={(delta) =>
                          step(index, field.draft as AttributeDraft, delta)
                        }
                      />
                    ) : isLiveBoolean(field, booleanControl) ? (
                      /*
                  A live control replaces the read-out **and** the editor: there
                  is no "open" state for a row whose whole value is one visible
                  bit, and `openIndex` is only used to give it focus when the
                  `Tab` chain arrives.

                  **The flip is a commit, not a shortcut past one.** It takes the
                  identical `open` → `commitAndClose` pair a typed edit and a
                  clear both take, so `validate` still gets to refuse it and a
                  rejected save still surfaces on the row itself. `open`
                  first because the hook decides "did this actually change?"
                  against the value the row was opened with, and it writes that
                  ref synchronously — the same ordering `onClear` relies on.
                */
                      <AttributeBooleanSegmented
                        field={field}
                        focused={openIndex === index}
                        onToggle={(next) => {
                          open(index);
                          commitAndClose(index, next ? 'yes' : 'no');
                        }}
                        onStep={(delta) =>
                          step(index, field.draft as AttributeDraft, delta)
                        }
                      />
                    ) : openIndex === index ? (
                      <AttributeFieldControl
                        field={field}
                        draft={draft}
                        unified={unified}
                        onDraft={setDraft}
                        onRevert={revert}
                        onCommit={(value) => commitAndClose(index, value)}
                        /*
                    `Tab` commits and advances — but only if the value was
                    accepted. A refused value keeps the field open with the
                    typing intact (§2): the refusal is known synchronously, so
                    stepping off it would throw the correction away in front of
                    the reader.
                  */
                        onStep={(value, delta) => step(index, value, delta)}
                      />
                    ) : (
                      <AttributeReadOut
                        /*
                          **The committed value, not the record's, for the
                          frames between the two** (VALE-1773). Everything else
                          on this read-out is the field as the host supplied it;
                          only the text it renders is bridged, and only while
                          `bridge` names this row. See
                          `attribute-draft-display.ts` for the render-order gap
                          and why the fix is a formatter rather than holding the
                          editor open.
                        */
                        field={bridgedField(field, bridge, index)}
                        bare={unified}
                        labelId={labelId}
                        onActivate={() => open(index)}
                        /*
                    A clear is **a commit, not a shortcut past one** — it goes
                    through the same `open` → `commitAndClose` pair a typed edit
                    does, so `validate` still gets to refuse it with a reason and
                    a rejected save still surfaces on the row. Clearing
                    is exactly the gesture most likely to be refused (it is how
                    a required field gets emptied), so routing it around the one
                    save path would put the silent-revert defect LF-6250 removed
                    straight back on the row beside it.

                    `open` first because the hook decides "did this actually
                    change?" against the value the field was opened with; it
                    writes that ref synchronously, so the commit on the next line
                    reads the current value rather than a stale one.

                    `!unified` because on the unified path the cell above renders
                    this affordance instead — passing it here as well would put
                    two `×` buttons on the row.
                  */
                        onClear={
                          !unified &&
                          offersClear(field as AttributeSpec<unknown>)
                            ? /* See the unified arm above (VALE-1247). */
                              (field.clearOverride ??
                              (() => {
                                open(index);
                                commitAndClose(index, clearDraft(field));
                              }))
                            : undefined
                        }
                      />
                    )}
                  </AttributeValueSlot>
                )}
                {/*
              **The refusal, on the row it belongs to** (VALE-349). It was one
              line above the whole grid, which had to name its field in prose
              because it had no other way to point at one — *"Office was not
              saved. …"* over a grid of nineteen rows.

              The label column already points, so the naming half of that
              sentence stays as screen-reader text rather than being deleted:
              the copy the commit path produces is unchanged (§2, "the existing
              failure copy is what it presents"), and a reader who cannot see
              which row is ringed still hears which field it was.

              `role="status"` and not `alert`: a refusal is the direct result of
              the reader's own gesture and the field is still in front of them,
              so it is a state to report, not an interruption.
            */}
                {failure?.label === field.label ? (
                  <p
                    role="status"
                    data-testid="lead-field-save-error"
                    /* A submit that stops on this row scrolls to it
                       (VALE-1476) — the row is the only thing saying what is
                       wrong, and it can be several screens above the button
                       the reviewer pressed. */
                    data-validation-error-field
                    className={ATTRIBUTE_ROW_ERROR}
                  >
                    <span className="sr-only">
                      {failure.label} was not saved.{' '}
                    </span>
                    {failure.message}
                  </p>
                ) : field.error ? (
                  /*
                  **The host's standing refusal, in the same slot** (VALE-1931).
                  A review's submit decided this value cannot be applied; the
                  message renders where a live refusal would and the cell wears
                  the same ring, so the reader is pointed at the field rather
                  than at the row holding it. The live refusal above wins when
                  both exist, so a field never says the same thing twice.
                */
                  <p
                    role="status"
                    data-testid="attribute-field-error"
                    data-validation-error-field
                    className={ATTRIBUTE_ROW_ERROR}
                  >
                    <span className="sr-only">{field.label}. </span>
                    {field.error}
                  </p>
                ) : field.note ? (
                  /*
                  **The row's standing advisory** (VALE-1008), in the refusal's
                  slot and one tone quieter — see {@link ATTRIBUTE_ROW_NOTE}.

                  Mutually exclusive with the refusal, and deliberately the loser:
                  a refusal is about the gesture the reader just made and is the
                  more urgent of the two. Stacking them would put two messages
                  under one value, and the note is still true once the refusal
                  clears.

                  `role="note"`, not `status`: unlike a refusal this is not the
                  result of anything the reader did, so it must not be announced
                  as a live update every time the grid re-renders.
                */
                  <p role="note" className={ATTRIBUTE_ROW_NOTE}>
                    {field.note}
                  </p>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
